import React from "react";

import { LeafIcon as Leaf, LockIcon } from "@packages/ds";
import { FormattedMessage, useIntl } from "react-intl";

import { OrganizationProjectStatusesTypes } from "Api/organizations/projects/OrganizationProjects.types";
import Tag from "ds/Tag";
import { getOrganizationId } from "Libs/utils";
import { selectFeatureBillingEnabled } from "Reducers/featureFlags/featureFlags.selectors";
import { useAppSelector } from "Store/hooks";

import { OrganizationProjectMoreLinks } from "./OrganizationProjectMoreLinks";
import * as S from "./ProjectDetailsCard.styles";
import { ProvisioningProjectCard } from "./ProjectProvisioning";
import { TrialTag } from "./TrialTag";
import { useProjectRegion } from "./useProjectRegion";

type Props = {
  hasProjectAccess: boolean;
  organizationLabel: string;
  projectId: string;
  projectRegion: string;
  projectStatus: string;
  projectSubscriptionId: string;
  projectTitle: string;
  projectType: string;
  organizationName: string;
  projectCreatedAt: string;
  refetchOrganizationProjectList: () => void;
};

const ProjectDetailsCard = ({
  hasProjectAccess,
  projectId,
  projectRegion,
  projectStatus,
  projectSubscriptionId,
  projectTitle,
  projectType,
  projectCreatedAt,
  organizationName,
  refetchOrganizationProjectList
}: Props) => {
  const intl = useIntl();
  const billingEnabled = useAppSelector(selectFeatureBillingEnabled);

  const { regionLabel, leafFill, carbonIntensity, isGreen } =
    useProjectRegion(projectRegion);

  const shouldShowSuspendedLabel =
    hasProjectAccess && projectStatus === "suspended";
  const organizationId = useAppSelector(state =>
    getOrganizationId(state, organizationName)
  );
  const projectIsProvisioning =
    projectStatus === OrganizationProjectStatusesTypes.requested;
  const projectIsInProvisioningFailure =
    projectStatus === OrganizationProjectStatusesTypes.failed;

  if (projectIsProvisioning || projectIsInProvisioningFailure) {
    return (
      <ProvisioningProjectCard
        organizationId={organizationId!}
        projectId={projectId}
        projectName={projectTitle}
        refetchOrganizationProjectList={refetchOrganizationProjectList}
        projectIsInProvisioningFailure={projectIsInProvisioningFailure}
        projectCreatedAt={projectCreatedAt}
      />
    );
  }

  const carbonIntensityNote =
    typeof carbonIntensity === "number" && carbonIntensity > 0
      ? intl.formatMessage(
          { id: "environment_impact.carbon_intensity" },
          {
            carbonIntensity
          }
        )
      : null;

  return (
    <S.Details>
      <S.TopRow>
        <S.ProjectNameContainer>
          <S.ProjectName tag="h3">
            {projectTitle || <FormattedMessage id="no_title" />}
          </S.ProjectName>
        </S.ProjectNameContainer>
        <OrganizationProjectMoreLinks
          projectId={projectId}
          organizationName={organizationName}
          projectSubscriptionId={projectSubscriptionId}
        />
      </S.TopRow>

      <S.Row>
        <S.Region>
          {regionLabel?.title || regionLabel.suffix ? (
            <S.RegionContent>
              {isGreen && (
                <S.Icon aria-label="leaf">
                  <Leaf fill={`var(${leafFill})`} width={24} height={24} />
                </S.Icon>
              )}
              <S.RegionColumn>
                <S.RegionTitle>
                  {regionLabel.title}
                  <S.RegionSuffix>{regionLabel.suffix}</S.RegionSuffix>
                </S.RegionTitle>
                <S.RegionCarbonIntensity>
                  {carbonIntensityNote}
                </S.RegionCarbonIntensity>
              </S.RegionColumn>
            </S.RegionContent>
          ) : (
            projectRegion
          )}
        </S.Region>

        {projectType === "dedicated" && (
          <Tag>
            <FormattedMessage id="dedicated" />
          </Tag>
        )}

        {!hasProjectAccess && (
          <S.NoAccessTag>
            <S.IconWrapper>
              <LockIcon color="var(--mode-vector-neutral-strong" />
            </S.IconWrapper>
            <FormattedMessage id="project.card.tag.no-access" />
          </S.NoAccessTag>
        )}

        {shouldShowSuspendedLabel ? (
          <Tag variant="red">
            <FormattedMessage id="project.card.tag.suspended" />
          </Tag>
        ) : (
          hasProjectAccess && billingEnabled && <TrialTag />
        )}
      </S.Row>
    </S.Details>
  );
};

export default React.memo(ProjectDetailsCard);
